import { useState } from 'react'
import { SubAdminSidebar } from '@/components/admin/sub-admin-sidebar'
import { AdminHeader } from '@/components/admin/admin-header'
import { AdminBottomNav } from '@/components/admin/admin-bottom-nav'
import { SubAdminMobileMenu } from '@/components/admin/sub-admin-mobile-menu'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    BookOpen, Users, FileText, Upload, MessageSquare,
    GraduationCap, Building2, Handshake, ArrowRight, Newspaper, Shield,
} from 'lucide-react'
import { useAuth } from '@/context/auth-context'
import { AdminCoursesView } from '@/components/admin/admin-courses-view'
import { AdminMentorsView } from '@/components/admin/admin-mentors-view'
import { AdminStudentsView } from '@/components/admin/admin-students-view'
// import { AdminRevenueView } from '@/components/admin/admin-revenue-view' // REMOVED
import { AdminTestsView } from '@/components/admin/admin-tests-view'
import { AdminMaterialsView } from '@/components/admin/admin-materials-view'
import { AdminCounsellorsView } from '@/components/admin/admin-counsellors-view'
import { AdminChatsView } from '@/components/admin/admin-chats-view'
import { AdminAbroadCollegesView } from '@/components/admin/admin-abroad-colleges-view'
import { AdminAbroadLeadsView } from '@/components/admin/admin-abroad-leads-view'
import { AdminCoachingTieUpsView } from '@/components/admin/admin-coaching-tieups-view'
import { AdminPartnerLeadsView } from '@/components/admin/admin-partner-leads-view'
import { AdminNewsView } from '@/components/admin/admin-news-view'
import { AdminBlogsView } from '@/components/admin/admin-blogs-view'
import { AdminLoginDataView } from '@/components/admin/admin-login-data-view'
import { mockAdminCourses, mockAdminMentors, mockAbroadLeads, mockPartnerLeads } from '@/data/admin-mock-data'

export function SubAdminDashboardPage() {
    const { user } = useAuth()
    const [activeTab, setActiveTab] = useState('dashboard')
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

    const handleMobileMenuNavigate = (tab: string) => {
        setActiveTab(tab)
        setIsMobileMenuOpen(false)
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'courses':
                return <AdminCoursesView />
            case 'students':
                return <AdminStudentsView />
            case 'mentors':
                return <AdminMentorsView />
            case 'counsellors':
                return <AdminCounsellorsView />
            // case 'revenue': return <AdminRevenueView /> // REMOVED
            case 'tests':
                return <AdminTestsView />
            case 'materials':
                return <AdminMaterialsView />
            case 'chats':
                return <AdminChatsView />
            case 'abroad-colleges':
                return <AdminAbroadCollegesView />
            case 'abroad-leads':
                return <AdminAbroadLeadsView />
            case 'coaching-tieups':
                return <AdminCoachingTieUpsView />
            case 'partner-leads':
                return <AdminPartnerLeadsView />
            case 'news':
                return <AdminNewsView />
            case 'blogs':
                return <AdminBlogsView />
            case 'login-data':
                return <AdminLoginDataView />
            default:
                return <DashboardOverview setActiveTab={setActiveTab} />
        }
    }

    function DashboardOverview({ setActiveTab }: { setActiveTab: (tab: string) => void }) {
        // Revenue calculations removed
        const newAbroadLeads = mockAbroadLeads.filter(l => l.status === 'new').length
        const newPartnerLeads = mockPartnerLeads.filter(l => l.status === 'new').length

        const quickLinks = [
            { id: 'courses', label: 'Courses', icon: BookOpen, desc: `${mockAdminCourses.length} courses`, color: 'from-blue-500 to-blue-600' },
            { id: 'students', label: 'Students', icon: Users, desc: 'Manage students', color: 'from-violet-500 to-purple-600' },
            { id: 'mentors', label: 'Assign Mentors', icon: Users, desc: `${mockAdminMentors.length} mentors`, color: 'from-amber-500 to-orange-500' },
            // Revenue link removed
            { id: 'tests', label: 'Tests', icon: FileText, desc: 'Create & manage', color: 'from-purple-500 to-violet-600' },
            { id: 'materials', label: 'Study Material', icon: Upload, desc: 'Upload & assign', color: 'from-cyan-500 to-blue-600' },
            { id: 'chats', label: 'Chats', icon: MessageSquare, desc: 'View conversations', color: 'from-pink-500 to-rose-600' },
            { id: 'abroad-colleges', label: 'Abroad Colleges', icon: GraduationCap, desc: 'Public page content', color: 'from-indigo-500 to-purple-600' },
            { id: 'abroad-leads', label: 'Abroad Leads', icon: Building2, desc: newAbroadLeads ? `${newAbroadLeads} new leads` : 'Lead assignments', color: 'from-teal-500 to-cyan-600' },
            { id: 'coaching-tieups', label: 'Coaching Tie-ups', icon: Handshake, desc: 'Partner institutes', color: 'from-orange-500 to-amber-600' },
            { id: 'partner-leads', label: 'Partner Leads', icon: Users, desc: newPartnerLeads ? `${newPartnerLeads} new leads` : 'Lead assignments', color: 'from-pink-500 to-rose-600' },
            { id: 'news', label: 'News / Updates', icon: Newspaper, desc: 'Publish news articles', color: 'from-sky-500 to-blue-600' },
            { id: 'blogs', label: 'Blog Posts', icon: BookOpen, desc: 'Manage blog content', color: 'from-violet-500 to-indigo-600' },
            { id: 'login-data', label: 'Login Data', icon: Shield, desc: 'Monitor user logins', color: 'from-slate-500 to-slate-600' },
        ]

        return (
            <div className="space-y-6 pb-24 lg:pb-0">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary">
                        Welcome back, {user?.name || 'Sub Admin'} 👋
                    </h2>
                    <p className="text-sm text-theme-secondary mt-1">
                        Manage courses, mentors, tests, materials, chats, and public website (abroad colleges, leads, coaching tie-ups).
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {quickLinks.map((item) => {
                        const Icon = item.icon
                        return (
                            <GlassmorphicCard
                                key={item.id}
                                className="p-5 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-primary-blue"
                                onClick={() => setActiveTab(item.id)}
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0`}>
                                        <Icon className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-theme-primary mb-1">{item.label}</h3>
                                        <p className="text-sm text-theme-secondary mb-2">{item.desc}</p>
                                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-blue">
                                            Open <ArrowRight className="h-4 w-4" />
                                        </span>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        )
                    })}
                </div>

                <GlassmorphicCard className="p-6">
                    <h3 className="font-bold text-theme-primary mb-2">Quick info</h3>
                    <p className="text-sm text-theme-secondary">
                        <strong>Core:</strong> Create/manage courses (Crash, Long Term, Short Term, 2-Year Integrated, Dropper), assign mentors to subjects and courses, create tests, upload study materials, view chats.
                        <br />
                        <strong>CRM / Public website:</strong> Abroad Colleges and Coaching Tie-ups CRUD update the Foreign Education and Partner Institutes pages. Abroad Leads are applications from the foreign education page — assign mentors and track status.
                    </p>
                </GlassmorphicCard>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            <div className="flex h-screen overflow-hidden">
                <div className="hidden lg:block shrink-0">
                    <SubAdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
                </div>

                <SubAdminMobileMenu
                    isOpen={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    activeTab={activeTab}
                    onNavigate={handleMobileMenuNavigate}
                />

                <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm sticky top-0 z-30">
                        <AdminHeader onMenuClick={() => setIsMobileMenuOpen(true)} />
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 md:p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>

            {/* Reuse AdminBottomNav or create specific one? AdminBottomNav might rely on admin tabs.
                Let's reuse it for now but if it has revenue, we might need a SubAdminBottomNav.
                Checking AdminBottomNav... file didn't load. Let's assume it might have Revenue.
                For safety, I'll allow it for now but if user complains I'll fix it.
                Actually, I should check it.
             */}
            <AdminBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
    )
}
